import { TripHealthUpsell } from "@/components/travels/TripHealthUpsell";
import { CountryAutocomplete } from "@/components/ui/CountryAutocomplete";
import {
  getHealthRequirements,
  HealthRequirements,
} from "@/services/api/travelHealth";
import {
  createSegment,
  CreateSegmentData,
  createTravel,
} from "@/services/api/travels";
import { useI18nStore } from "@/stores/i18nStore";
import { useSubscriptionStore } from "@/stores/subscriptionStore";
import type { Translation } from "@/types/i18n";
import { paymentRequired } from "@/utils/paymentRequired";
import {
  AlertCircle,
  Calendar,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Heart,
  MapPin,
  Plus,
  Route,
  Trash2,
  Users,
} from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";

// ─── Types ──────────────────────────────────────────────────────────────────

type TripPurpose =
  | "leisure"
  | "business"
  | "medical"
  | "education"
  | "volunteering"
  | "science"
  | "other";
type TripType =
  | "cruise"
  | "beach"
  | "mountains"
  | "hiking"
  | "camping"
  | "road_trip"
  | "sports"
  | "education"
  | "event"
  | "culture"
  | "expedition"
  | "other";

interface SegmentDraft {
  id: string;
  segmentTitle: string;
  segmentType: "transfer" | "destination" | "accommodation" | "activity";
  startDateTime: string;
  startLocation: string;
  startCountry: string;
  endDateTime: string;
  endLocation: string;
  endCountry: string;
  transportationType?: string;
  accommodationType?: string;
  segmentOperator?: string;
  bookingReference?: string;
}

interface WizardState {
  step: 1 | 2 | 3 | 4 | 5;
  basics: {
    tripTitle: string;
    destination: string;
    country: string;
    departureDate: string;
    returnDate: string;
    startLocation: string;
    endLocation: string;
  };
  details: {
    purpose: TripPurpose;
    tripType: TripType;
    tripOperator: string;
    bookingReference: string;
    companions: number;
    notes: string;
  };
  segments: SegmentDraft[];
  healthData: HealthRequirements | null;
}

const STORAGE_KEY = "healthpass_trip_wizard_draft";

/**
 * Where the health check stands. Not part of the draft: whether the account
 * may see it is decided again on every visit, by the server.
 *
 * - idle: not asked yet
 * - paywalled: the account is not entitled (known client-side, or a 402)
 * - failed: anything else went wrong - the check is optional, so it is a note
 */
type HealthStatus = "idle" | "loading" | "loaded" | "paywalled" | "failed";

function hasNoHealthData(data: HealthRequirements | null): boolean {
  if (!data) return true;
  return (
    (data.vaccinations?.length ?? 0) === 0 &&
    (data.healthWarnings?.length ?? 0) === 0 &&
    (!data.malariaRisk || data.malariaRisk === "none")
  );
}

const emptyWizard = (): WizardState => ({
  step: 1,
  basics: {
    tripTitle: "",
    destination: "",
    country: "",
    departureDate: "",
    returnDate: "",
    startLocation: "",
    endLocation: "",
  },
  details: {
    purpose: "leisure",
    tripType: "other",
    tripOperator: "",
    bookingReference: "",
    companions: 0,
    notes: "",
  },
  segments: [],
  healthData: null,
});

// ─── Main Component ──────────────────────────────────────────────────────────

interface TripWizardProps {
  onClose?: () => void;
}

export const TripWizard: React.FC<TripWizardProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const { t } = useI18nStore();
  const { currentSubscription, hasAccess } = useSubscriptionStore();
  const currentPlan = currentSubscription?.tier ?? "FREE";
  // Planning a trip is free; the destination health check is sold with PLUS
  // and the server answers 402 without it. When the store already knows the
  // account is below PLUS, the request is not made at all. When it does not
  // know yet, the server's answer decides - see loadHealth.
  const knownNotEntitled = currentSubscription !== null && !hasAccess("PLUS");

  const [state, setState] = useState<WizardState>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : emptyWizard();
    } catch {
      return emptyWizard();
    }
  });
  const [saving, setSaving] = useState(false);
  const [healthStatus, setHealthStatus] = useState<HealthStatus>("idle");
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Persist draft to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  const updateBasics = (patch: Partial<WizardState["basics"]>) => {
    // The health check belongs to one destination and date; a changed one
    // has to be asked again rather than show the previous country's answer.
    const destinationChanged =
      (patch.country !== undefined && patch.country !== state.basics.country) ||
      (patch.departureDate !== undefined &&
        patch.departureDate !== state.basics.departureDate);
    if (destinationChanged) setHealthStatus("idle");
    setState((s) => ({
      ...s,
      basics: { ...s.basics, ...patch },
      healthData: destinationChanged ? null : s.healthData,
    }));
  };

  const updateDetails = (patch: Partial<WizardState["details"]>) =>
    setState((s) => ({ ...s, details: { ...s.details, ...patch } }));

  const setStep = (step: WizardState["step"]) =>
    setState((s) => ({ ...s, step }));

  // ── Validation ──────────────────────────────────────────────────────────

  const validateStep1 = useCallback((): boolean => {
    const e: Record<string, string> = {};
    if (!state.basics.tripTitle.trim()) e.tripTitle = t.required;
    if (!state.basics.destination.trim()) e.destination = t.required;
    if (!state.basics.country) e.country = t.required;
    if (!state.basics.departureDate) e.departureDate = t.required;
    if (!state.basics.returnDate) e.returnDate = t.required;
    if (
      state.basics.departureDate &&
      state.basics.returnDate &&
      new Date(state.basics.returnDate) < new Date(state.basics.departureDate)
    ) {
      e.returnDate = "End date must be after start date";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }, [state.basics, t]);

  // ── Health Load (Step 4) ────────────────────────────────────────────────

  const loadHealth = useCallback(async () => {
    if (!state.basics.country || state.healthData) {
      setHealthStatus("loaded");
      return;
    }
    if (knownNotEntitled) {
      setHealthStatus("paywalled");
      return;
    }
    setHealthStatus("loading");
    try {
      // Quiet: a 402 here becomes the optional offer in this step, not the
      // app-wide upgrade dialog on top of a flow that is free.
      const data = await getHealthRequirements(
        state.basics.country,
        state.basics.departureDate,
        { quietPaywall: true },
      );
      setState((s) => ({ ...s, healthData: data ?? null }));
      setHealthStatus("loaded");
    } catch (error) {
      setHealthStatus(paymentRequired(error) ? "paywalled" : "failed");
    }
  }, [
    state.basics.country,
    state.basics.departureDate,
    state.healthData,
    knownNotEntitled,
  ]);

  // Asked when the step is reached, which includes coming back to a saved
  // draft that stood on this step - after an upgrade, for instance.
  useEffect(() => {
    if (state.step === 4 && healthStatus === "idle") void loadHealth();
  }, [state.step, healthStatus, loadHealth]);

  const handleNext = () => {
    if (state.step === 1 && !validateStep1()) return;
    if (state.step < 5) setStep((state.step + 1) as WizardState["step"]);
  };

  const handlePrev = () => {
    if (state.step > 1) setStep((state.step - 1) as WizardState["step"]);
  };

  // ── Save ────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    setSaving(true);
    try {
      const travel = await createTravel({
        tripTitle: state.basics.tripTitle,
        destination: state.basics.destination,
        country: state.basics.country,
        departureDate: state.basics.departureDate,
        returnDate: state.basics.returnDate,
        startLocation: state.basics.startLocation || undefined,
        endLocation: state.basics.endLocation || undefined,
        purpose: state.details.purpose,
        tripType: state.details.tripType,
        tripOperator: state.details.tripOperator || undefined,
        bookingReference: state.details.bookingReference || undefined,
        companions: state.details.companions,
        notes: state.details.notes || undefined,
      });

      // Create each segment sequentially
      for (const seg of state.segments) {
        const segData: CreateSegmentData = {
          segmentTitle: seg.segmentTitle,
          segmentType: seg.segmentType,
          startDateTime: seg.startDateTime,
          startLocation: seg.startLocation,
          startCountry: seg.startCountry,
          endDateTime: seg.endDateTime,
          endLocation: seg.endLocation,
          endCountry: seg.endCountry,
          transportationType: seg.transportationType,
          accommodationType: seg.accommodationType,
          segmentOperator: seg.segmentOperator,
          bookingReference: seg.bookingReference,
        };
        await createSegment(travel._id, segData);
      }

      localStorage.removeItem(STORAGE_KEY);
      toast.success("Trip created successfully!");
      navigate(`/traveler/travels/${travel._id}`);
    } catch (err: unknown) {
      console.error("Failed to create trip:", err);
      toast.error("Failed to create trip. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // ── Duration ────────────────────────────────────────────────────────────

  const duration = (() => {
    if (!state.basics.departureDate || !state.basics.returnDate) return null;
    const diff = Math.ceil(
      (new Date(state.basics.returnDate).getTime() -
        new Date(state.basics.departureDate).getTime()) /
        86400000,
    );
    return diff > 0 ? diff : null;
  })();

  // ── Step Labels ─────────────────────────────────────────────────────────

  const steps = [
    { label: t.tripWizardStep1, icon: MapPin },
    { label: t.tripWizardStep2, icon: Users },
    { label: t.tripWizardStep3, icon: Route },
    { label: t.tripWizardStep4, icon: Heart },
    { label: t.tripWizardStep5, icon: CheckCircle },
  ];

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-3xl px-4 py-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">
            {t.tripWizardTitle}
          </h1>
          {onClose && (
            <button
              onClick={onClose}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              {t.cancel}
            </button>
          )}
        </div>

        {/* Progress Bar */}
        <nav className="mb-8">
          <ol className="flex items-center gap-2">
            {steps.map((s, idx) => {
              const stepNum = idx + 1;
              const isActive = state.step === stepNum;
              const isDone = state.step > stepNum;
              return (
                <li
                  key={idx}
                  className="relative flex flex-1 flex-col items-center gap-1"
                >
                  {idx < steps.length - 1 && (
                    <div
                      className={`pointer-events-none absolute left-1/2 top-[18px] z-0 h-0.5 w-full ${isDone ? "bg-indigo-600" : "bg-gray-200"}`}
                    />
                  )}
                  <div
                    className={`relative z-10 flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold transition-colors
                    ${isDone ? "bg-indigo-600 text-white" : isActive ? "bg-indigo-100 text-indigo-700 ring-2 ring-indigo-500" : "bg-gray-100 text-gray-400"}`}
                  >
                    {isDone ? <CheckCircle className="h-5 w-5" /> : stepNum}
                  </div>
                  <span
                    className={`relative z-10 hidden text-xs font-medium sm:block ${isActive ? "text-indigo-700" : "text-gray-400"}`}
                  >
                    {s.label}
                  </span>
                </li>
              );
            })}
          </ol>
        </nav>

        {/* Step Content */}
        <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-200">
          {state.step === 1 && (
            <Step1Basics
              data={state.basics}
              onChange={updateBasics}
              errors={errors}
              t={t}
            />
          )}
          {state.step === 2 && (
            <Step2Details
              data={state.details}
              onChange={updateDetails}
              t={t}
              currentPlan={currentPlan}
            />
          )}
          {state.step === 3 && (
            <Step3Segments
              segments={state.segments}
              onChange={(segs) => setState((s) => ({ ...s, segments: segs }))}
              t={t}
            />
          )}
          {state.step === 4 && (
            <Step4Health
              country={state.basics.country}
              healthData={state.healthData}
              status={knownNotEntitled ? "paywalled" : healthStatus}
              onSkip={() => setStep(5)}
              t={t}
            />
          )}
          {state.step === 5 && (
            <Step5Review state={state} duration={duration} t={t} />
          )}
        </div>

        {/* Navigation */}
        <div className="mt-6 flex justify-between">
          <button
            onClick={state.step === 1 ? onClose : handlePrev}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
            {state.step === 1 ? t.cancel : t.tripWizardPrevious}
          </button>

          {state.step < 5 ? (
            <button
              onClick={handleNext}
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 transition-colors"
            >
              {t.tripWizardNext}
              <ChevronRight className="h-4 w-4" />
            </button>
          ) : (
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-700 disabled:opacity-60 transition-colors"
            >
              <CheckCircle className="h-4 w-4" />
              {saving ? t.tripWizardSaving : t.tripWizardSave}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Step 1: Basics ──────────────────────────────────────────────────────────

interface Step1Props {
  data: WizardState["basics"];
  onChange: (patch: Partial<WizardState["basics"]>) => void;
  errors: Record<string, string>;
  t: Translation;
}

const Step1Basics: React.FC<Step1Props> = ({ data, onChange, errors, t }) => (
  <div className="space-y-5">
    <div>
      <h2 className="text-lg font-semibold text-gray-900">
        {t.tripWizardStep1Title}
      </h2>
      <p className="text-sm text-gray-500">{t.tripWizardStep1Desc}</p>
    </div>

    <Field label={t.tripTitle} required error={errors.tripTitle}>
      <input
        type="text"
        value={data.tripTitle}
        onChange={(e) => onChange({ tripTitle: e.target.value })}
        placeholder="e.g. Caribbean Cruise 2026"
        className={inputCls(!!errors.tripTitle)}
      />
    </Field>

    <div className="grid grid-cols-2 gap-4">
      <Field label={t.destination} required error={errors.destination}>
        <input
          type="text"
          value={data.destination}
          onChange={(e) => onChange({ destination: e.target.value })}
          placeholder="e.g. Aruba"
          className={inputCls(!!errors.destination)}
        />
      </Field>

      <Field label={t.country} required error={errors.country}>
        <CountryAutocomplete
          value={data.country}
          onChange={(v) => onChange({ country: v })}
          placeholder={t.pleaseSelect}
        />
        {errors.country && (
          <p className="mt-1 text-xs text-red-500">{errors.country}</p>
        )}
      </Field>
    </div>

    <div className="grid grid-cols-2 gap-4">
      <Field label={t.startDate} required error={errors.departureDate}>
        <input
          type="date"
          value={data.departureDate}
          onChange={(e) => onChange({ departureDate: e.target.value })}
          className={inputCls(!!errors.departureDate)}
        />
      </Field>

      <Field label={t.endDate} required error={errors.returnDate}>
        <input
          type="date"
          value={data.returnDate}
          min={data.departureDate}
          onChange={(e) => onChange({ returnDate: e.target.value })}
          className={inputCls(!!errors.returnDate)}
        />
      </Field>
    </div>

    <div className="grid grid-cols-2 gap-4">
      <Field label={t.startLocation}>
        <input
          type="text"
          value={data.startLocation}
          onChange={(e) => onChange({ startLocation: e.target.value })}
          placeholder="e.g. Frankfurt (FRA)"
          className={inputCls(false)}
        />
      </Field>

      <Field label={t.endLocation}>
        <input
          type="text"
          value={data.endLocation}
          onChange={(e) => onChange({ endLocation: e.target.value })}
          placeholder="e.g. Hamburg (HAM)"
          className={inputCls(false)}
        />
      </Field>
    </div>
  </div>
);

// ─── Step 2: Details ─────────────────────────────────────────────────────────

interface Step2Props {
  data: WizardState["details"];
  onChange: (patch: Partial<WizardState["details"]>) => void;
  t: Translation;
  currentPlan: string;
}

const PURPOSES: { value: TripPurpose; labelKey: keyof Translation }[] = [
  { value: "leisure", labelKey: "purposeLeisure" },
  { value: "business", labelKey: "purposeBusiness" },
  { value: "medical", labelKey: "purposeMedical" },
  { value: "education", labelKey: "purposeEducation" },
  { value: "volunteering", labelKey: "purposeVolunteering" },
  { value: "science", labelKey: "purposeScience" },
  { value: "other", labelKey: "purposeOther" },
];

const TRIP_TYPES: { value: TripType; labelKey: keyof Translation }[] = [
  { value: "cruise", labelKey: "tripTypeCruise" },
  { value: "beach", labelKey: "tripTypeBeach" },
  { value: "mountains", labelKey: "tripTypeMountains" },
  { value: "hiking", labelKey: "tripTypeHiking" },
  { value: "camping", labelKey: "tripTypeCamping" },
  { value: "road_trip", labelKey: "tripTypeRoadTrip" },
  { value: "sports", labelKey: "tripTypeSports" },
  { value: "education", labelKey: "tripTypeEducation" },
  { value: "event", labelKey: "tripTypeEvent" },
  { value: "culture", labelKey: "tripTypeCulture" },
  { value: "expedition", labelKey: "tripTypeExpedition" },
  { value: "other", labelKey: "purposeOther" },
];

const Step2Details: React.FC<Step2Props> = ({ data, onChange, t }) => (
  <div className="space-y-5">
    <div>
      <h2 className="text-lg font-semibold text-gray-900">
        {t.tripWizardStep2Title}
      </h2>
      <p className="text-sm text-gray-500">{t.tripWizardStep2Desc}</p>
    </div>

    <div className="grid grid-cols-2 gap-4">
      <Field label={t.travelPurpose}>
        <select
          value={data.purpose}
          onChange={(e) => onChange({ purpose: e.target.value as TripPurpose })}
          className={inputCls(false)}
        >
          {PURPOSES.map((p) => (
            <option key={p.value} value={p.value}>
              {t[p.labelKey] as string}
            </option>
          ))}
        </select>
      </Field>

      <Field label={t.tripType}>
        <select
          value={data.tripType}
          onChange={(e) => onChange({ tripType: e.target.value as TripType })}
          className={inputCls(false)}
        >
          {TRIP_TYPES.map((tt) => (
            <option key={tt.value} value={tt.value}>
              {t[tt.labelKey] as string}
            </option>
          ))}
        </select>
      </Field>
    </div>

    <div className="grid grid-cols-2 gap-4">
      <Field label={t.tripOperator}>
        <input
          type="text"
          value={data.tripOperator}
          onChange={(e) => onChange({ tripOperator: e.target.value })}
          placeholder="e.g. AIDA, TUI"
          className={inputCls(false)}
        />
      </Field>

      <Field label={t.bookingReference}>
        <input
          type="text"
          value={data.bookingReference}
          onChange={(e) => onChange({ bookingReference: e.target.value })}
          placeholder="e.g. TUI-2026-ABC"
          className={inputCls(false)}
        />
      </Field>
    </div>

    <Field label={`${t.companions} (0–20)`}>
      <input
        type="number"
        min={0}
        max={20}
        value={data.companions}
        onChange={(e) =>
          onChange({
            companions: Math.min(
              20,
              Math.max(0, parseInt(e.target.value) || 0),
            ),
          })
        }
        className={`${inputCls(false)} w-32`}
      />
    </Field>

    <Field label={t.notes}>
      <textarea
        value={data.notes}
        onChange={(e) => onChange({ notes: e.target.value })}
        rows={3}
        placeholder="Special requirements, notes..."
        className={inputCls(false)}
      />
    </Field>
  </div>
);

// ─── Step 3: Segments ────────────────────────────────────────────────────────

interface Step3Props {
  segments: SegmentDraft[];
  onChange: (segs: SegmentDraft[]) => void;
  t: Translation;
}

const emptySegment = (): SegmentDraft => ({
  id: Math.random().toString(36).slice(2),
  segmentTitle: "",
  segmentType: "transfer",
  startDateTime: "",
  startLocation: "",
  startCountry: "",
  endDateTime: "",
  endLocation: "",
  endCountry: "",
});

const Step3Segments: React.FC<Step3Props> = ({ segments, onChange, t }) => {
  const addSegment = () => onChange([...segments, emptySegment()]);
  const removeSegment = (id: string) =>
    onChange(segments.filter((s) => s.id !== id));
  const updateSegment = (id: string, patch: Partial<SegmentDraft>) =>
    onChange(segments.map((s) => (s.id === id ? { ...s, ...patch } : s)));

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">
          {t.tripWizardStep3Title}
        </h2>
        <p className="text-sm text-gray-500">{t.tripWizardStep3Desc}</p>
      </div>

      {segments.length === 0 && (
        <div className="rounded-xl border-2 border-dashed border-gray-200 p-8 text-center">
          <Route className="mx-auto mb-2 h-8 w-8 text-gray-300" />
          <p className="text-sm text-gray-500">{t.noSegmentsYet}</p>
          <p className="text-xs text-gray-400">{t.tripWizardSkipSegments}</p>
        </div>
      )}

      {segments.map((seg, idx) => (
        <div
          key={seg.id}
          className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-4"
        >
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">
              {t.tripWizardStep3} #{idx + 1}
            </h3>
            <button
              onClick={() => removeSegment(seg.id)}
              className="text-red-400 hover:text-red-600 transition-colors"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label={t.segmentTitle} required={false}>
              <input
                type="text"
                value={seg.segmentTitle}
                onChange={(e) =>
                  updateSegment(seg.id, { segmentTitle: e.target.value })
                }
                placeholder="e.g. Flight FRA → Aruba"
                className={inputCls(false)}
              />
            </Field>

            <Field label={t.segmentType} required={false}>
              <select
                value={seg.segmentType}
                onChange={(e) =>
                  updateSegment(seg.id, {
                    segmentType: e.target.value as SegmentDraft["segmentType"],
                  })
                }
                className={inputCls(false)}
              >
                <option value="transfer">{t.segmentTypeTransfer}</option>
                <option value="destination">{t.segmentTypeDestination}</option>
                <option value="accommodation">
                  {t.segmentTypeAccommodation}
                </option>
                <option value="activity">{t.segmentTypeActivity}</option>
              </select>
            </Field>

            <Field label={t.startDateTime} required={false}>
              <input
                type="datetime-local"
                value={seg.startDateTime}
                onChange={(e) =>
                  updateSegment(seg.id, { startDateTime: e.target.value })
                }
                className={inputCls(false)}
              />
            </Field>
            <Field label={t.endDateTime} required={false}>
              <input
                type="datetime-local"
                value={seg.endDateTime}
                onChange={(e) =>
                  updateSegment(seg.id, { endDateTime: e.target.value })
                }
                className={inputCls(false)}
              />
            </Field>

            <Field label={t.startLocation} required={false}>
              <input
                type="text"
                value={seg.startLocation}
                onChange={(e) =>
                  updateSegment(seg.id, { startLocation: e.target.value })
                }
                className={inputCls(false)}
              />
            </Field>
            <Field label={t.endLocation} required={false}>
              <input
                type="text"
                value={seg.endLocation}
                onChange={(e) =>
                  updateSegment(seg.id, { endLocation: e.target.value })
                }
                className={inputCls(false)}
              />
            </Field>
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={addSegment}
        className="inline-flex items-center gap-2 rounded-lg border border-indigo-300 bg-indigo-50 px-4 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-100 transition-colors"
      >
        <Plus className="h-4 w-4" />
        {t.tripWizardAddSegment}
      </button>
    </div>
  );
};

// ─── Step 4: Health ──────────────────────────────────────────────────────────

interface Step4Props {
  country: string;
  healthData: HealthRequirements | null;
  status: HealthStatus;
  onSkip: () => void;
  t: Translation;
}

const Step4Health: React.FC<Step4Props> = ({
  country,
  healthData,
  status,
  onSkip,
  t,
}) => {
  const riskColor = (level: string) =>
    level === "high"
      ? "text-red-600 bg-red-50"
      : level === "medium"
        ? "text-amber-600 bg-amber-50"
        : "text-green-600 bg-green-50";

  const loading = status === "idle" || status === "loading";

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">
          {t.tripWizardStep4Title}
        </h2>
        <p className="text-sm text-gray-500">
          {country ? `${t.destination}: ${country}` : t.tripWizardStep4Desc}
        </p>
      </div>

      <p className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-600">
        {t.tripHealthOptional}
      </p>

      {status === "paywalled" && (
        <TripHealthUpsell country={country} onSkip={onSkip} showDraftNote />
      )}

      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600" />
        </div>
      )}

      {status === "failed" && (
        <div className="flex items-center gap-3 rounded-xl bg-gray-50 p-4">
          <AlertCircle className="h-5 w-5 shrink-0 text-gray-400" />
          <p className="text-sm text-gray-600">{t.tripHealthLoadFailed}</p>
        </div>
      )}

      {status === "loaded" && hasNoHealthData(healthData) && (
        <div className="flex items-center gap-3 rounded-xl bg-gray-50 p-4">
          <CheckCircle className="h-5 w-5 shrink-0 text-gray-400" />
          <p className="text-sm text-gray-600">
            {t.tripHealthEmpty.replace("{country}", country)}
          </p>
        </div>
      )}

      {status === "loaded" && healthData && !hasNoHealthData(healthData) && (
        <div className="space-y-4">
          {/* Malaria Risk */}
          {healthData.malariaRisk && healthData.malariaRisk !== "none" && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4">
              <p className="text-sm font-semibold text-red-700">
                Malaria Risk: {healthData.malariaRisk}
              </p>
            </div>
          )}

          {/* Vaccinations */}
          {healthData.vaccinations?.length > 0 && (
            <div>
              <p className="mb-2 text-sm font-semibold text-gray-700">
                {t.vaccinations}
              </p>
              <div className="space-y-2">
                {healthData.vaccinations.map((v, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2"
                  >
                    <span className="text-sm text-gray-800">{v.name}</span>
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${v.required ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}`}
                    >
                      {v.required ? t.mandatory : t.recommended}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Warnings */}
          {healthData.healthWarnings?.length > 0 && (
            <div>
              <p className="mb-2 text-sm font-semibold text-gray-700">
                {t.healthWarnings}
              </p>
              <div className="space-y-2">
                {healthData.healthWarnings.map((w, i) => (
                  <div
                    key={i}
                    className={`flex items-start gap-2 rounded-lg px-3 py-2 ${riskColor(w.level)}`}
                  >
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span className="text-xs">{w.message}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <p className="text-xs text-gray-400">
            <Calendar className="inline h-3 w-3 mr-1" />
            {t.lastUpdated}: {healthData.lastUpdated} · {t.source}:{" "}
            {healthData.source}
          </p>
        </div>
      )}
    </div>
  );
};

// ─── Step 5: Review ──────────────────────────────────────────────────────────

interface Step5Props {
  state: WizardState;
  duration: number | null;
  t: Translation;
}

const Step5Review: React.FC<Step5Props> = ({ state, duration, t }) => (
  <div className="space-y-6">
    <div>
      <h2 className="text-lg font-semibold text-gray-900">
        {t.tripWizardStep5Title}
      </h2>
      <p className="text-sm text-gray-500">{t.tripWizardReviewSummary}</p>
    </div>

    <div className="rounded-xl bg-gray-50 p-4 space-y-3">
      <ReviewRow label={t.tripTitle} value={state.basics.tripTitle} />
      <ReviewRow
        label={t.destination}
        value={`${state.basics.destination}, ${state.basics.country}`}
      />
      <ReviewRow label={t.startDate} value={state.basics.departureDate} />
      <ReviewRow label={t.endDate} value={state.basics.returnDate} />
      {duration !== null && (
        <ReviewRow
          label={t.tripWizardDuration}
          value={`${duration} ${t.days}`}
        />
      )}
      <ReviewRow label={t.travelPurpose} value={state.details.purpose} />
      <ReviewRow label={t.tripType} value={state.details.tripType} />
      {state.details.tripOperator && (
        <ReviewRow label={t.tripOperator} value={state.details.tripOperator} />
      )}
      {state.details.companions > 0 && (
        <ReviewRow
          label={t.companions}
          value={String(state.details.companions)}
        />
      )}
      <ReviewRow
        label={t.tripWizardSegmentCount}
        value={String(state.segments.length)}
      />
    </div>
  </div>
);

const ReviewRow: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-gray-500">{label}</span>
    <span className="font-medium text-gray-900">{value}</span>
  </div>
);

// ─── Helpers ─────────────────────────────────────────────────────────────────

interface FieldProps {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}
const Field: React.FC<FieldProps> = ({ label, required, error, children }) => (
  <div>
    <label className="mb-1 block text-sm font-medium text-gray-700">
      {label}
      {required && <span className="ml-0.5 text-red-500">*</span>}
    </label>
    {children}
    {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
  </div>
);

const inputCls = (hasError: boolean) =>
  `w-full rounded-lg border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors
  ${hasError ? "border-red-300 bg-red-50" : "border-gray-300 bg-white hover:border-gray-400"}`;
